// Story 7.9 (ADO-1141) — basket state in a Zustand store (AC1/AC2/AC3); Story 8.2
// (ADO-1093) — the durable server write (`POST /basket/items`) now backs it.
//
// project-context React rules (ADR-004): Zustand for all client-side global
// state. The SERVER is the source of truth: every mutation returns the full
// post-state basket, which replaces the store (favourites/ADR-004 rule), except
// for a line whose own write is still in flight or queued (Story 8.2). `add` is
// OPTIMISTIC for an immediate inline confirmation, then reconciles against the real
// persisted basket the server returns; on any failure it rolls back and surfaces
// the reason (Story 8.2 replaced the pre-durable client-only stopgap).
//
// Mirrors `favourites/useFavouritesStore.ts` — the ONLY semantic change is
// QUANTITY: favourites is a Set toggle, a basket line is a SKU→qty map that
// INCREMENTS (AC2: a second add of the same SKU bumps its quantity, never a
// duplicate line). Two projections of the same data:
//   • `lines` — the full basket, for a future basket screen (Story 8.x).
//   • `quantityBySku` — a Map for the per-card affordance (cards subscribe with
//     `s.quantityBySku.get(sku)`, the shape by which they read availability).
//
// `add` is OPTIMISTIC so the card confirms immediately (AC1 inline confirmation,
// no navigation); on the server response the store reconciles to truth, and on a
// failure it puts the acting SKU's row back as it stood before the change, applied
// to CURRENT state so every other row is kept (favourites review lesson: never
// restore a stale snapshot of the whole basket). It records the reason under the
// acting SKU in `mutationErrors`, a per-SKU map so a failed add on one SKU cannot
// surface (or clear) the reason on another concurrent add.

import { create } from 'zustand';
import {
  addFromPreviousOrder,
  addToBasket,
  armAutoSend,
  AutoSendRefusedError,
  disarmAutoSend,
  BasketLineConflictError,
  CartAlreadySentError,
  CartChangedSinceSeenError,
  CartSendingError,
  BasketServerError,
  clearBasket,
  listBasket,
  removeBasketLine,
  updateBasketLineQuantity,
  type AddFromPreviousOrderRequest,
  type AutoSendChoices,
  type AutoSendState,
  type BasketLine,
  type BasketState,
  type PreviousOrderLineNotAdded,
  type PreviousOrderLineQuantity,
} from '../api/basket.client';
import { useAvailabilityStore } from '../availability/useAvailabilityStore';
import { useSessionStore } from '../auth/useSessionStore';
import { cartTargetKey, type CartTarget } from '../api/cart-target';
import { sessionCartKey, sessionCartTarget } from './sessionCart';
import { MAX_LINE_QUANTITY } from './lineQuantity';
import { sentSinceSeen, type SeenCart } from './sentSinceSeen';
import { rememberShownRevision, shownRevisionOf } from './shownCartRevision';

/** AC3 — the inline block message when a one-click add is attempted on a SKU the
 *  supplier marks `UNAVAILABLE` (DD-S.2 axis 1). Kept in the store so a blocked
 *  `add` surfaces the same reason the card shows, defence-in-depth behind the
 *  disabled button. The AUTHORITATIVE server enforcement is Story 8.2 (D-3).
 *  The IDENTIFIER says `SUPPLIER_UNAVAILABLE` (axis 1), NOT `OUT_OF_STOCK` (the
 *  advisory band) so it can't be mistaken for the band gate (DD-S.2 hazard); the
 *  customer-facing COPY keeps the familiar "out of stock" wording. */
export const SUPPLIER_UNAVAILABLE_MESSAGE =
  'This product is out of stock and cannot be added to your basket.';

/** Story 8.1 (ADO-1092), AC1 — the depot-selection gate message. A basket is
 *  scoped to a depot, so nothing can be added until one is chosen. Surfaced by a
 *  blocked `add` as defence-in-depth behind the card's disabled button + the
 *  header "Select a depot" prompt. */
export const NO_DEPOT_SELECTED_MESSAGE = 'Select a depot before adding products to your basket.';

/** Story 8.7 (ADO-2016) — what `addFromOrder` did, for the order card that asked. */
export type AddFromOrderOutcome =
  /** The server wrote what it could; `notAdded` lists every line it refused (AC4). */
  | {
      readonly kind: 'added';
      readonly added: readonly PreviousOrderLineQuantity[];
      readonly notAdded: readonly PreviousOrderLineNotAdded[];
    }
  /** Nothing was written; `message` is safe to show the Shopper. */
  | { readonly kind: 'failed'; readonly message: string }
  /** Story 8.3 (ADO-2012), Task 6 (card 07): a REPLACE refused because the cart changed since
   *  the Shopper was shown it and still has lines. Nothing was written; the store now shows
   *  the cart as it is, and the counts are for the dialog. */
  | { readonly kind: 'changed'; readonly lineCount: number; readonly caseCount: number }
  /** Another basket write was in flight, so nothing was sent. */
  | { readonly kind: 'busy' }
  /** The depot changed while the request was in flight; its answer was dropped. */
  | { readonly kind: 'dropped' };

/**
 * Story 8.3 (ADO-2012), Task 10.4 (D-5, D-6): a single add, set or remove of this product was
 * refused because the cart line was not the one the Shopper saw. The UI shows the Conflict
 * Message from it (cards 01 to 05). It stays until the Shopper taps the message's action,
 * changes that line again, or leaves the page; never on a timer, and not when something else
 * on the page changes (the designer's lifetime rule, Task 11.2).
 */
export interface LineConflict {
  /** What the Shopper asked for: an add, a quantity change, or a removal (a change to 0).
   *  Story 8.3 (ADO-2012), card 09: `review`, a line that changed while the Shopper reviewed
   *  the cart, marked after a refused send (`requestedQuantity` is the reviewed quantity). */
  readonly action: 'add' | 'set' | 'remove' | 'review';
  /** The number they picked: the quantity added, the quantity set, or 0 for a removal. */
  readonly requestedQuantity: number;
  /** Whether their view held a line for the product (an add of a product they saw none of
   *  is "this was just added", card 01). */
  readonly sawLine: boolean;
  /** The line's quantity now, or `null` when it was removed. */
  readonly currentQuantity: number | null;
  /** Task 11.5: the row as the Shopper saw it, so a removed line can be drawn where it was
   *  (card 03): its name, and the product of the row it followed (`null`: it was first).
   *  `null` when no row was shown for the product. */
  readonly lastShown: { readonly name: string | null; readonly after: string | null } | null;
}

/** Story 8.3 (ADO-2012), Task 13.4 (card 10): the change the server refused because it was made
 *  from a view of the cart before its latest send (Enabler E8-G, ADO-3109, DA-1). ADO-3257 (story
 *  D-6): or a read of the cart that named a send made after the cart the page showed, with no
 *  product (`skuCode`, `productName` and `quantity` all `null`): no change was refused. */
export interface OrderSentNotice {
  /** When the cart was sent (ISO 8601 UTC). */
  readonly sentAt: string;
  /** The product of the refused change; `null` for a read. */
  readonly skuCode: string | null;
  /** The product as the row named it, or its code; `null` for a read. */
  readonly productName: string | null;
  /** The number the Shopper picked, for "Add to a new cart" (the emptied cart, AC8); `null` for
   *  a removal (nothing to add, decided with the user) and for a read. */
  readonly quantity: number | null;
  /** When the refusal was noted (epoch ms), so the banner works out "today" without reading the
   *  clock while it renders. */
  readonly notedAt: number;
}

/** Enabler E8-G (ADO-3109), AC16: what a cart kept from its latest accepted send, as a read of
 *  that cart (`hydrate`) returned it, with the cart it was read for (`cartTargetKey`). */
export interface KeptFromSend {
  readonly cartKey: string;
  readonly customerOrderReference: string | null;
  /** `YYYY-MM-DD`, or `null`. */
  readonly requestedDeliveryDate: string | null;
}

/** Story 8.15 (ADO-2024): why the last Auto-send change did not land, worded for the Shopper.
 *  `reference`: the account needs a (valid) order reference; `fulfilment`: a delivery or
 *  collection choice was refused; `unavailable`: the cart's depot runs no cut-off; `failed`:
 *  anything else (a lost connection, a changed cart). */
export interface AutoSendError {
  readonly kind: 'reference' | 'fulfilment' | 'unavailable' | 'failed';
  readonly message: string;
}

/** Story 8.15 (ADO-2024): the `x-shoplink-problem-type` slugs an Auto-send write is refused with
 *  (the send's own, order-capture's `CUSTOMER_ORDER_REFERENCE_PROBLEM`, `ORDER_FULFILMENT_PROBLEM`
 *  and `CART_CHANGED_PROBLEM`, plus `auto-send-unavailable`). */
const AUTO_SEND_REFERENCE_PROBLEM = 'customer-order-reference';
const AUTO_SEND_FULFILMENT_PROBLEM = 'order-fulfilment';
const AUTO_SEND_UNAVAILABLE_PROBLEM = 'auto-send-unavailable';
const AUTO_SEND_CART_CHANGED_PROBLEM = 'cart-changed';

/** Story 8.15 (ADO-2024): the message for a failed Auto-send change. `armed` is whether the cart
 *  was armed when the change started: a refused re-save leaves the earlier choices armed. */
function autoSendErrorOf(error: unknown, arming: boolean, armed: boolean): AutoSendError {
  if (error instanceof AutoSendRefusedError) {
    const kept = armed && arming ? 'Auto-send kept your earlier choices. ' : '';
    if (error.problemType === AUTO_SEND_REFERENCE_PROBLEM) {
      return { kind: 'reference', message: `${kept}${error.message}` };
    }
    if (error.problemType === AUTO_SEND_FULFILMENT_PROBLEM) {
      const fieldMessages = error.invalidFields.map((refusal) => refusal.message).join(' ');
      return { kind: 'fulfilment', message: `${kept}${fieldMessages || error.message}` };
    }
    if (error.problemType === AUTO_SEND_UNAVAILABLE_PROBLEM) {
      return { kind: 'unavailable', message: 'Auto-send is not available for this order.' };
    }
    if (error.problemType === AUTO_SEND_CART_CHANGED_PROBLEM) {
      return { kind: 'failed', message: 'Your cart changed. Try again.' };
    }
    return { kind: 'failed', message: error.message };
  }
  if (!arming) return { kind: 'failed', message: 'Auto-send could not be turned off. Try again.' };
  return {
    kind: 'failed',
    message: armed
      ? 'Auto-send could not save your changes. Try again.'
      : 'Auto-send could not be turned on. Try again.',
  };
}

/** Story 8.3 (ADO-2012), Task 13.2: how long the store waits before trying a write once more
 *  that a send held. A send is usually done well inside it. */
const SEND_RETRY_DELAY_MS = 1000;
let sendRetryDelayMs = SEND_RETRY_DELAY_MS;

/** Story 8.3 (ADO-2012): the wait before the one retry, for specs (`null` restores it). */
export function setSendRetryDelayForTests(ms: number | null): void {
  sendRetryDelayMs = ms ?? SEND_RETRY_DELAY_MS;
}

/** Story 8.3 (ADO-2012), Task 13.2: the wait before trying once more what a send's hold refused
 *  (a line write here, a submit in `useOrderSubmissionStore`). */
export function waitForTheSendHold(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, sendRetryDelayMs));
}

/** Story 8.3 (ADO-2012), Task 13.2: `send`, tried once more after a short wait when a send held
 *  the cart; any other outcome, or a second hold, is the caller's to handle. The retry goes out
 *  only while `stillCurrent()` holds after the wait: once the store was reset (a depot or
 *  account switch, a sign-out) the cart is no longer on screen, so the hold's refusal stands
 *  and the caller drops it. Story 11.12 (ADO-1963): also the WOW add's (`addToWowOrder`), so the
 *  held-cart rule lives here only. */
export async function withOneRetryIfSending<T>(
  send: () => Promise<T>,
  stillCurrent: () => boolean,
): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (!(error instanceof CartSendingError)) throw error;
    await waitForTheSendHold();
    if (!stillCurrent()) throw error;
    return send();
  }
}

interface BasketStoreState {
  /** Full basket lines — server truth after each reconcile. */
  lines: readonly BasketLine[];
  /** Story 8.3 (ADO-2012), D-11: the cart the lines are (`null`: none open) and its revision,
   *  from the newest response applied; an older response for the same cart is not applied. */
  cartId: string | null;
  revision: number;
  /** Story 8.3 (ADO-2012), Task 10.4: the refused writes, by product, for the UI. */
  lineConflicts: ReadonlyMap<string, LineConflict>;
  /** Story 8.3 (ADO-2012), Task 11.2: the Shopper tapped the message's action (or closed
   *  it): drop that product's conflict. */
  dismissConflict: (skuCode: string) => void;
  /** Story 8.3 (ADO-2012), Task 11.2: the Shopper left the page: drop every conflict. */
  dismissConflicts: () => void;
  /** Story 8.3 (ADO-2012), Task 13.4 (card 10): a change was refused because the cart it named
   *  had been sent; the Order sent banner's facts. `null` otherwise. It lasts until its action
   *  is tapped, the cart is changed again, or the Shopper leaves the page. */
  orderSent: OrderSentNotice | null;
  /** Story 8.3 (ADO-2012), Task 13.4: the banner's action was tapped, or the page left. */
  dismissOrderSent: () => void;
  /** Enabler E8-G (ADO-3109), DA-1: when the cart on screen was last sent, as a line write's
   *  answer told this store (card 10's `sentAt`, card 11's `previousCartSentAt`), or ADO-3257 a
   *  read of the cart or card 09's cart that named a send made after the cart shown; `null`
   *  until one does, and after a reset. A cart keeps its id after a send (AC8), so this, not a
   *  change of cart id, is how the store learns a colleague sent it. */
  cartSentAt: string | null;
  /** Enabler E8-G (ADO-3109), AC16: what the cart on screen kept from its latest accepted send,
   *  from the latest read of it; `null` before a read that carries it, and after a reset. */
  keptFromSend: KeptFromSend | null;
  /** Story 8.3 (ADO-2012), Task 13.4 (card 10): "Add to a new cart": add the number the Shopper
   *  picked to the cart the send emptied (the same cart, AC8). The banner stays while the add is
   *  in flight and ends once it lands; a failed add leaves it, with the add's error under its
   *  product. Resolves whether the add landed. */
  addToNewCart: () => Promise<boolean>;
  /** Story 8.3 (ADO-2012), Task 13.5 (card 11): when the cart was sent, if the last add of
   *  `skuCode` was made from a view of the cart before that send (it went into the emptied cart);
   *  asked once, then forgotten. `null` for any other add. */
  takeNewCartStarted: (skuCode: string) => string | null;
  /** Story 8.3 (ADO-2012), Task 13.1 (card 09, D-24): a send was refused because the cart
   *  changed while the Shopper reviewed it. Show `current` and mark each line that changed, was
   *  removed or was added since `reviewed` (the lines the Shopper reviewed); returns how many.
   *  Marks already shown stay (the lifetime rule): only lines that changed again are re-marked. */
  markChangedWhileReviewing: (
    reviewed: readonly BasketLine[],
    current: BasketState,
    /** The store's `generation()` when the send started: after a reset since, the refusal is
     *  for a cart no longer on screen, so nothing changes and the answer is `null`. */
    startedIn: number,
  ) => number | null;
  /** Story 8.3 (ADO-2012): bumped by every `reset()`, so a caller that awaits something can tell
   *  whether the cart it started with is still the one on screen. */
  generation: () => number;
  /** Resolves once every cart write in flight now (a line write, a clear, a previous-order add)
   *  has settled, whatever its outcome. A reset drops a write's answer but not its request, so a
   *  caller that leaves a cart can wait here before reading what the server holds for it. */
  writesSettled: () => Promise<void>;
  /** ADO-3257 (story D-5): card 09's cart named a send made after the revision the refused send
   *  carried: record when the cart was sent, as a write's answer does. Nothing after a reset since
   *  `startedIn` (the cart is no longer on screen). */
  noteCartSent: (sentAt: string, startedIn: number) => void;
  /** SKU → quantity projection — drives the per-card Add affordance. */
  quantityBySku: ReadonlyMap<string, number>;
  /** True once the initial basket has loaded. */
  hydrated: boolean;
  /** True when the last `hydrate` failed — lets a screen offer a retry rather
   *  than an infinite spinner (favourites review lesson). */
  error: boolean;
  /** SKUs with an add in flight — a per-SKU mutex so a double-click cannot race
   *  two full-post-state reconciles to a wrong final state (favourites lesson). */
  pending: ReadonlySet<string>;
  /** Per-SKU add errors — SKU → the user-facing message for the last add of that
   *  SKU that FAILED or was BLOCKED. Keyed by SKU (not a single slot) so a
   *  failed/blocked add on one SKU can neither raise a false "Added" on another
   *  concurrent add nor clear another SKU's real error. A card reads its own SKU's
   *  entry (`mutationErrors.get(sku)`) to show the reason beside the button so a
   *  failed/blocked add isn't silent. A SKU's entry is cleared when a new add for
   *  that SKU starts or succeeds; other SKUs' entries are left untouched. */
  mutationErrors: ReadonlyMap<string, string>;
  /** Story 8.7 (ADO-2016): true while an `addFromOrder` request is in flight. */
  addingFromOrder: boolean;
  /** Load the basket of `cart` (Story 8.2 ADO-2011, AC5; Story 8.4 ADO-2013: the depot
   *  and the account): after the cart is opened, and again on a switch. A read that lands
   *  after a basket reset (a change of depot or account) is dropped, and so are the lines
   *  of a read that a successful clear overtook (Story 8.9, ADO-2018). */
  hydrate: (cart: CartTarget) => Promise<void>;
  /** Add `quantity` (default 1) of a SKU: optimistic add-at-quantity / summed
   *  increment, then reconcile to the server's total (Story 8.2 ADO-2011, AC1/AC2);
   *  no-op + surfaced block on a supplier-UNAVAILABLE SKU (AC3). No-op for a
   *  quantity that is not a positive integer. */
  add: (skuCode: string, quantity?: number) => Promise<void>;
  /** Story 8.2 (ADO-2011): set a line's ABSOLUTE quantity (PATCH; 0 removes the
   *  line). Optimistic, reconciled to the server state, and on failure only THIS
   *  row is rolled back with the server's message. No-op for a SKU not in the
   *  basket or a quantity that is not a non-negative integer. Resolves `true` when
   *  the server accepted the edit and `false` when it did not (a no-op, a failure,
   *  or a failure dropped after a depot switch), so a caller can put focus back on
   *  the row. An edit made while another one for the same SKU is in flight is
   *  queued: shown at once, and only the latest queued quantity is sent once the
   *  in-flight request settles (AC4). */
  setQuantity: (skuCode: string, quantity: number) => Promise<boolean>;
  /** Story 8.2 (ADO-2011): remove a line (DELETE). Same contract as `setQuantity`;
   *  a failed remove restores the row in its original position. */
  remove: (skuCode: string) => Promise<boolean>;
  /** Story 8.9 (ADO-2018): true while a whole-cart clear is in flight (its mutex). */
  clearing: boolean;
  /** Story 8.9 (ADO-2018): the user-facing reason the last clear failed, or null. */
  clearError: string | null;
  /** Story 8.3 (ADO-2012), Task 6 (card 06, D-21): the last clear was refused because the cart
   *  changed since the Shopper was shown it; its line and case counts now, for the dialog.
   *  `null` otherwise. The store already shows the cart as it is. */
  clearChanged: { readonly lineCount: number; readonly caseCount: number } | null;
  /** Story 8.3 (ADO-2012), D-21: the Shopper cancelled the card 06 dialog. */
  dismissClearChanged: () => void;
  /** Story 8.9 (ADO-2018): clear the cart for everyone on it (DELETE with
   *  `confirm=true`), called only after the Shopper confirmed. NON-optimistic: the
   *  lines stay until the server answers, so a failure leaves the cart exactly as it
   *  was. Resolves `true` only when the server cleared the session's cart and
   *  the store now holds its (empty) state; `false` with no depot, while a clear is
   *  already in flight, while a line write is still pending (no error is set), on a
   *  failure, or when the depot changed mid-flight. */
  clear: () => Promise<boolean>;
  /** Story 8.7 (ADO-2016): bring a previous order's lines into the basket (AC1-AC3). NOT
   *  optimistic: the server decides what is added, so the lines are replaced from its
   *  answer. Refused (`busy`) while another basket write is in flight; an answer that
   *  lands after a depot switch is dropped. */
  addFromOrder: (
    orderId: string,
    request: AddFromPreviousOrderRequest,
  ) => Promise<AddFromOrderOutcome>;
  /** Story 8.15 (ADO-2024), D-3: the cart's Auto-send opt-in, from the latest read of the cart or
   *  the latest Auto-send write that landed; `null` when it is not armed (or not known). */
  autoSend: AutoSendState | null;
  /** Story 8.15 (ADO-2024), D-1: the lead time the cart read named; `null` until a read names it,
   *  and the control is hidden until then (its copy is derived from it). */
  autoSendLeadMinutes: number | null;
  /** Story 8.15 (ADO-2024): the state the Shopper last asked for while a write is in flight (the
   *  checkbox shows it); `null` with nothing in flight. */
  autoSendPending: boolean | null;
  /** Story 8.15 (ADO-2024): why the last Auto-send change did not land, or `null`. */
  autoSendError: AutoSendError | null;
  /** Story 8.15 (ADO-2024), D-3, D-5, D-7: arm the session cart's Auto-send with `choices` (or
   *  re-save them while armed), or disarm it (`armed` false; `choices` unused). A change asked
   *  for while one is in flight is held, and only the latest is sent once it settles. A change of
   *  cart drops the answer and anything held. Nothing is sent with no open cart. */
  setAutoSend: (armed: boolean, choices: AutoSendChoices | null) => Promise<void>;
  /** Story 8.15 (ADO-2024): forget the last Auto-send failure's message. */
  dismissAutoSendError: () => void;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

function quantityMap(lines: readonly BasketLine[]): ReadonlyMap<string, number> {
  return new Map(lines.map((l) => [l.skuCode, l.quantity]));
}

/** `line` at a new quantity, optimistically. The locked unit price
 *  stays (a quantity change never re-prices a line) but the stored `lineTotal` is dropped:
 *  it belongs to the old quantity, so the cart value is worked out from the unit price
 *  until the server answers with the new total. ADO-2246: the same for the deposit, the locked
 *  `depositPerUnit` stays and the stored `lineDeposit` is dropped. */
function withLineQuantity(line: BasketLine, quantity: number): BasketLine {
  const { lineTotal, lineDeposit, ...rest } = line;
  void lineTotal;
  void lineDeposit;
  return { ...rest, quantity };
}

/** Optimistic +`by`: bump the line's quantity, or append it at `by` (AC1/AC2 — a
 *  SKU already in the basket increments its SAME line, never a duplicate; Story 8.2
 *  ADO-2011 sums the entered quantity onto it). */
function withIncrement(
  lines: readonly BasketLine[],
  sku: string,
  by: number,
): readonly BasketLine[] {
  if (lines.some((l) => l.skuCode === sku)) {
    return lines.map((l) => (l.skuCode === sku ? withLineQuantity(l, l.quantity + by) : l));
  }
  return [...lines, { skuCode: sku, quantity: by }];
}

function withSku(set: ReadonlySet<string>, sku: string): ReadonlySet<string> {
  const next = new Set(set);
  next.add(sku);
  return next;
}

function withoutSku(set: ReadonlySet<string>, sku: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(sku);
  return next;
}

/** Set this SKU's add-error message, replacing the map immutably (matches the
 *  `withSku`/`withIncrement` build-a-new-collection style). Only the acting SKU's
 *  entry changes; every other SKU's error is preserved. */
function withError(
  errors: ReadonlyMap<string, string>,
  sku: string,
  message: string,
): ReadonlyMap<string, string> {
  const next = new Map(errors);
  next.set(sku, message);
  return next;
}

/** Clear this SKU's add-error entry, replacing the map immutably. Other SKUs'
 *  errors are left in place — the whole point of keying by SKU. */
function withoutError(
  errors: ReadonlyMap<string, string>,
  sku: string,
): ReadonlyMap<string, string> {
  const next = new Map(errors);
  next.delete(sku);
  return next;
}

/** Story 8.3 (ADO-2012): the conflicts without this SKU's, replacing the map immutably. */
function withoutConflict(
  conflicts: ReadonlyMap<string, LineConflict>,
  sku: string,
): ReadonlyMap<string, LineConflict> {
  if (!conflicts.has(sku)) return conflicts;
  const next = new Map(conflicts);
  next.delete(sku);
  return next;
}

/** Set one line's quantity (0 drops the line), leaving every other row as-is. */
function withQuantity(
  lines: readonly BasketLine[],
  sku: string,
  quantity: number,
): readonly BasketLine[] {
  if (quantity === 0) return lines.filter((l) => l.skuCode !== sku);
  return lines.map((l) => (l.skuCode === sku ? withLineQuantity(l, quantity) : l));
}

/** Where a row sat before an optimistic edit: its neighbours' SKUs (null at an
 *  end of the list) plus its index as the last resort. */
interface RowPosition {
  readonly index: number;
  readonly before: string | null;
  readonly after: string | null;
}

/** One SKU's row as it stood before an optimistic change. */
interface RowSnapshot {
  readonly row: BasketLine;
  readonly position: RowPosition;
}

/** The SKU's row and where it sits, or null when the SKU is not in the basket. */
function snapshotRow(lines: readonly BasketLine[], sku: string): RowSnapshot | null {
  const index = lines.findIndex((l) => l.skuCode === sku);
  const row = lines[index];
  if (row === undefined) return null;
  return {
    row,
    position: {
      index,
      before: lines[index - 1]?.skuCode ?? null,
      after: lines[index + 1]?.skuCode ?? null,
    },
  };
}

/** Make ONE SKU's row match a snapshot, leaving every other row as-is: the row
 *  is restored, or dropped when the snapshot holds none. */
function withRowAsSnapshot(
  lines: readonly BasketLine[],
  sku: string,
  snapshot: RowSnapshot | null,
): readonly BasketLine[] {
  if (snapshot === null) return lines.filter((l) => l.skuCode !== sku);
  return withRestoredRow(lines, snapshot.row, snapshot.position);
}

/** Put ONE row back to its pre-edit value against CURRENT state (a concurrent
 *  edit on another SKU is kept). A row the optimistic edit removed is re-inserted
 *  relative to its neighbours as they stand NOW: after the row that preceded it,
 *  else before the row that followed it, else at its original index clamped to the
 *  current length. A concurrent add or remove elsewhere cannot shift it into the
 *  wrong slot. */
function withRestoredRow(
  lines: readonly BasketLine[],
  previous: BasketLine,
  position: RowPosition,
): readonly BasketLine[] {
  if (lines.some((l) => l.skuCode === previous.skuCode)) {
    return lines.map((l) => (l.skuCode === previous.skuCode ? previous : l));
  }
  const beforeIndex = lines.findIndex((l) => l.skuCode === position.before);
  const afterIndex = lines.findIndex((l) => l.skuCode === position.after);
  const insertAt =
    beforeIndex !== -1
      ? beforeIndex + 1
      : afterIndex !== -1
        ? afterIndex
        : Math.min(position.index, lines.length);
  const next = [...lines];
  next.splice(insertAt, 0, previous);
  return next;
}

/** Reconcile to a server state, except for the SKUs whose own write is still in
 *  flight or queued: they keep the row the Shopper sees now (or its absence), and
 *  their own response settles them. Without this, another line's response would
 *  put back a quantity the Shopper has since changed, and the next stepper tap
 *  would be worked out from it (AC4). */
function withUnsettledRowsKept(
  serverLines: readonly BasketLine[],
  currentLines: readonly BasketLine[],
  unsettled: Iterable<string>,
): readonly BasketLine[] {
  let next = serverLines;
  for (const sku of unsettled) {
    next = withRowAsSnapshot(next, sku, snapshotRow(currentLines, sku));
  }
  return next;
}

/** Surface ONLY a message the server authored for the user (a `BasketServerError`
 *  from the Nest 400 body). Every other failure — a network error ("Failed to
 *  fetch"), a malformed-2xx shape error, a raw TypeError — is internal text that
 *  must not reach the card's `role="alert"`, so it falls back to a generic line. */
function messageOf(err: unknown): string {
  return err instanceof BasketServerError && err.message.length > 0
    ? err.message
    : 'Something went wrong. Please try again.';
}

/** Story 8.2 (ADO-2011) — one optimistic line mutation, as `mutateLine` runs it.
 *  The per-SKU mutex, depot gate, stale-write drop, rollback and error
 *  bookkeeping are shared; only the optimistic transform differs per caller. */
interface LineMutation {
  skuCode: string;
  /** A client-side block checked after the depot gate (a message means no-op). */
  blockedReason?: () => string | null;
  /** The optimistic change applied to the lines before the request. */
  applyOptimistic: (lines: readonly BasketLine[]) => readonly BasketLine[];
  /** Story 8.3 (ADO-2012): what the Shopper asked for, recorded if the write is refused. */
  action: LineConflict['action'];
  requestedQuantity: number;
  /** The server write, carrying the session cart resolved when the mutation started
   *  (Story 8.2; Story 8.4 ADO-2013: the depot and the account) and, Story 8.3 (ADO-2012),
   *  the token of the SKU's row the write is made from (`null`: no row). */
  send: (
    cart: CartTarget,
    seenToken: string | null,
    /** Enabler E8-G (ADO-3109), DA-1: the revision of the cart on screen when the write started,
     *  `undefined` with no cart on screen. */
    seenRevision: number | undefined,
  ) => Promise<BasketState>;
  /** Story 8.3 (ADO-2012), Task 13.4: this write is the Order sent banner's own add, so starting
   *  it does not end the banner (its success does, in `addToNewCart`). */
  keepsOrderSent?: boolean;
}

/** Story 8.2 (ADO-2011), AC4: the latest quantity a Shopper asked for while an
 *  edit of that SKU was in flight, and the callers waiting to learn whether it
 *  landed. */
interface QueuedEdit {
  quantity: number;
  readonly waiters: ((landed: boolean) => void)[];
}

/** Story 8.3 (ADO-2012): a quantity change to `quantity` as the Shopper meant it: a change to
 *  0 is a removal (card 04), anything else a change (card 02). */
function editIntent(quantity: number): Pick<LineMutation, 'action' | 'requestedQuantity'> {
  return { action: quantity === 0 ? 'remove' : 'set', requestedQuantity: quantity };
}

/** Enabler E8-G (ADO-3109), AC16: what a read of `cart` says the cart kept from its latest send,
 *  or `null` when the read does not carry it. */
function keptFromSendOf(state: BasketState, cart: CartTarget): KeptFromSend | null {
  const { customerOrderReference, requestedDeliveryDate } = state;
  if (customerOrderReference === undefined && requestedDeliveryDate === undefined) return null;
  return {
    cartKey: cartTargetKey(cart),
    customerOrderReference: customerOrderReference ?? null,
    requestedDeliveryDate: requestedDeliveryDate ?? null,
  };
}

export const useBasketStore = create<BasketStoreState>((set, get) => {
  /** Per-SKU queued edits (AC4: a stepper tap is never dropped). Held outside the
   *  store state because the optimistic quantity is already in `lines`; this only
   *  remembers what to send once the in-flight request settles. */
  const queuedEdits = new Map<string, QueuedEdit>();
  /** Story 8.3 (ADO-2012), Task 13.5 (card 11): per product, when the cart was sent, when the
   *  product's last add was made from a view before that send. Taken once by the add's
   *  confirmation. */
  const newCartStartedBySku = new Map<string, string>();

  /** Bumped by every `reset()` (so by every change of depot or account). A read or write settles
   *  store state only while the generation it started in is still current: once a
   *  reset has cleared the lines, `pending` and `queuedEdits`, anything in them
   *  belongs to a newer request. Story 8.9 (ADO-2018): the same holds for a clear,
   *  whose settle must not touch even the `clearing` mutex a newer clear may now
   *  hold. The depot check alone misses an A -> B -> A switch. */
  let resetGeneration = 0;

  /** Story 8.9 (ADO-2018): bumped when a clear's DELETE succeeds and its (empty)
   *  state is applied. A read that started before that holds the pre-clear lines,
   *  so `hydrate` drops them. A failed or superseded clear changed nothing on the
   *  server, so it leaves this alone. Line writes and `addFromOrder` need no such
   *  check: the `clearing` mutex keeps them from overlapping a clear at all. */
  let clearGeneration = 0;

  /** Story 8.15 (ADO-2024), D-3/D-7: bumped when an Auto-send arm or disarm succeeds and its
   *  answer is applied. Arming does not move the cart revision, so a read that started before
   *  that holds the opt-in as it was before the write, and `hydrate` keeps the opt-in shown. A
   *  failed or superseded write changed nothing here, so it leaves this alone. */
  let autoSendGeneration = 0;

  /** Story 8.15 (ADO-2024): the latest Auto-send change asked for while one was in flight. */
  let queuedAutoSend: { readonly armed: boolean; readonly choices: AutoSendChoices | null } | null =
    null;

  /** Every cart write still in flight, across resets (see `writesSettled`). */
  const writesInFlight = new Set<Promise<unknown>>();

  function tracked<T>(write: Promise<T>): Promise<T> {
    writesInFlight.add(write);
    const forget = (): void => {
      writesInFlight.delete(write);
    };
    write.then(forget, forget);
    return write;
  }

  function takeQueuedEdit(skuCode: string): QueuedEdit | undefined {
    const queued = queuedEdits.get(skuCode);
    queuedEdits.delete(skuCode);
    return queued;
  }

  /** Drop a SKU's queued edit (if any), telling its callers whether it landed. */
  function settleQueuedEdit(skuCode: string, landed: boolean): void {
    takeQueuedEdit(skuCode)?.waiters.forEach((resolve) => resolve(landed));
  }

  /**
   * AC4: an edit made while another one for the same SKU is in flight. The row
   * shows the new quantity at once and only the latest one is sent, after the
   * in-flight request settles (last write wins). Resolves once that send settles.
   */
  function queueEdit(skuCode: string, quantity: number): Promise<boolean> {
    const { lines, mutationErrors } = get();
    if (!lines.some((l) => l.skuCode === skuCode)) return Promise.resolve(false);
    const optimistic = withQuantity(lines, skuCode, quantity);
    set({
      lines: optimistic,
      quantityBySku: quantityMap(optimistic),
      mutationErrors: withoutError(mutationErrors, skuCode),
      lineConflicts: withoutConflict(get().lineConflicts, skuCode),
    });
    return new Promise((resolve) => {
      const queued = queuedEdits.get(skuCode);
      if (queued === undefined) {
        queuedEdits.set(skuCode, { quantity, waiters: [resolve] });
        return;
      }
      queued.quantity = quantity;
      queued.waiters.push(resolve);
    });
  }

  /** Send a queued quantity on top of the state the previous write returned
   *  (other lines' unsettled rows already kept). Its rollback restores the row as
   *  that server state holds it. */
  async function sendQueuedEdit(
    skuCode: string,
    queued: QueuedEdit,
    cart: CartTarget,
    serverLines: readonly BasketLine[],
  ): Promise<void> {
    if (!serverLines.some((l) => l.skuCode === skuCode)) {
      // The server no longer holds the line, so there is nothing left to edit.
      set({
        lines: serverLines,
        quantityBySku: quantityMap(serverLines),
        pending: withoutSku(get().pending, skuCode),
      });
      queued.waiters.forEach((resolve) => resolve(false));
      return;
    }
    const { quantity } = queued;
    const landed = await runLine(
      {
        skuCode,
        applyOptimistic: (current) => withQuantity(current, skuCode, quantity),
        ...editIntent(quantity),
        // Story 8.3 (ADO-2012), D-6: the token the previous response returned for the row.
        send: (sendCart, seenToken, seenRevision) =>
          updateBasketLineQuantity(skuCode, quantity, sendCart, seenToken ?? '', seenRevision),
      },
      cart,
      serverLines,
    );
    queued.waiters.forEach((resolve) => resolve(landed));
  }

  /**
   * Story 8.2 (ADO-2011) — the single mutation path behind `add`, `setQuantity`
   * and `remove`, so the guards cannot drift between them: per-SKU mutex, depot
   * gate, optimistic update, reconcile to the returned state, a drop of a write
   * started before the last `reset()` on BOTH the success and the failure path
   * (park-and-switch, sign-out), and a rollback of the acting SKU's
   * row with the reason recorded under it. Resolves `true` only when the server
   * accepted the write.
   */
  async function mutateLine(mutation: LineMutation): Promise<boolean> {
    const { skuCode } = mutation;
    const { lines, pending, mutationErrors } = get();
    // A mutation for this SKU is already in flight, so ignore this one: two
    // full-post-state reconciles must not race to a wrong final state. A cart
    // line edit never reaches here while pending (`setQuantity`/`remove` queue it
    // instead); only a repeated one-click `add` does.
    if (pending.has(skuCode)) return false;
    // Story 8.9 (ADO-2018) — a whole-cart clear is in flight: this write's later
    // reconcile would resurrect a line after the clear emptied the basket, so refuse
    // (the bidirectional mutex; `clear()` refuses while any line write is pending).
    if (get().clearing) return false;

    // Story 8.1 (ADO-1092), AC1 — depot-selection gate (defence-in-depth behind
    // the disabled controls + the header prompt). A basket is scoped to a cart; the
    // resolved cart (the depot, and since Story 8.3 the account) is carried into the
    // write so the stateless server resolves the caller's exact DRAFT cart (Story 8.2).
    const cart = sessionCartTarget();
    if (cart === null) {
      set({ mutationErrors: withError(mutationErrors, skuCode, NO_DEPOT_SELECTED_MESSAGE) });
      return false;
    }

    const blocked = mutation.blockedReason?.() ?? null;
    if (blocked !== null) {
      set({ mutationErrors: withError(mutationErrors, skuCode, blocked) });
      return false;
    }

    return runLine(mutation, cart, lines);
  }

  /**
   * Story 8.2 (ADO-2011): apply one mutation optimistically on `baseLines`, send
   * it, and reconcile. When a newer quantity for the same SKU was queued while the
   * request was in flight, the SKU stays pending and that quantity is sent next
   * (last write wins). A queued edit is settled `false` when this write fails; a
   * write started before the last `reset()` settles nothing, since the reset has
   * already settled its queued edit and freed its lock. On failure the SKU's row goes
   * back to how `baseLines` held it, whatever was queued or reconciled over it in
   * the meantime.
   */
  async function runLine(
    mutation: LineMutation,
    cart: CartTarget,
    baseLines: readonly BasketLine[],
  ): Promise<boolean> {
    const { skuCode } = mutation;
    const startedIn = resetGeneration;
    const before = snapshotRow(baseLines, skuCode);
    // Story 8.3 (ADO-2012), D-5/D-6: the write is made from this row, so it carries its token
    // (a row the store created optimistically has none; an add of a product with no row sends
    // none). A queued edit's base lines are the previous response's, so it carries that token.
    const seenToken = before?.row.token ?? null;
    // Enabler E8-G (ADO-3109), DA-1 (cards 10 and 11): the revision on screen, so the server can
    // tell a change made from a view of the cart before its latest send (none: no cart on screen,
    // a first add or after a switch, and the server then checks nothing). A queued edit starts
    // after the previous answer was applied, so it carries that answer's revision.
    const seenRevision = revisionShown();
    // Optimistic: clear THIS SKU's stale error as we retry so it doesn't linger;
    // every other SKU's error is preserved (the per-SKU keying). A reconcile that
    // lands leaves the map as-is, so a success has already dropped its own entry.
    // Story 8.3 (ADO-2012), Task 11.2: a new change to the line also ends its conflict.
    const optimistic = mutation.applyOptimistic(baseLines);
    set({
      lines: optimistic,
      quantityBySku: quantityMap(optimistic),
      pending: withSku(get().pending, skuCode),
      mutationErrors: withoutError(get().mutationErrors, skuCode),
      lineConflicts: withoutConflict(get().lineConflicts, skuCode),
      // Story 8.3 (ADO-2012), Task 13.4: a new change to the cart ends the Order sent banner.
      ...(mutation.keepsOrderSent === true ? {} : { orderSent: null }),
    });
    try {
      const state = await tracked(
        withOneRetryIfSending(
          () => mutation.send(cart, seenToken, seenRevision),
          () => startedIn === resetGeneration,
        ),
      );
      // Park-and-switch (Story 8.1): the store was reset while the request was in
      // flight (a depot switch, even one switched back, or a sign-out), so the
      // response is a basket no longer on screen. Drop it without touching
      // `pending` or `queuedEdits`: the reset cleared both, so what they hold now
      // belongs to a newer write for this SKU.
      if (startedIn !== resetGeneration) return true;
      // Story 8.3 (ADO-2012), Task 13.5 (card 11): the add was made from a view before the cart's
      // latest send, so it went into the emptied cart. Each write of the product replaces what an
      // earlier one recorded, so a record no confirmation read (a Removed Line's "Add it back")
      // never reaches a later, usual add (D-28).
      if (state.previousCartSentAt !== undefined) {
        newCartStartedBySku.set(skuCode, state.previousCartSentAt);
        set({ cartSentAt: state.previousCartSentAt });
      } else {
        newCartStartedBySku.delete(skuCode);
      }
      const reconciled = withUnsettledRowsKept(
        orderedLines(state, skuCode),
        get().lines,
        withoutSku(get().pending, skuCode),
      );
      const queued = takeQueuedEdit(skuCode);
      if (queued !== undefined) {
        set(identityOf(state));
        void sendQueuedEdit(skuCode, queued, cart, reconciled);
        return true;
      }
      set({
        lines: reconciled,
        quantityBySku: quantityMap(reconciled),
        pending: withoutSku(get().pending, skuCode),
        ...identityOf(state),
      });
      return true;
    } catch (err) {
      // Same guard as the success path: the failed write belongs to a basket no
      // longer on screen, so rolling it back, surfacing its error or settling the
      // lock and queue would corrupt the one now displayed, including a line it
      // holds for the same SKU.
      if (startedIn !== resetGeneration) return false;
      // A queued edit was worked out from a value the server did not accept, so it
      // is dropped with this write; the row rolls back and shows the reason.
      settleQueuedEdit(skuCode, false);
      // Story 8.3 (ADO-2012), card 11 (D-28): this write did not go into the emptied cart, so an
      // earlier add's record for the product, not yet confirmed, is no longer the latest.
      newCartStartedBySku.delete(skuCode);
      if (err instanceof CartAlreadySentError) {
        // Story 8.3 (ADO-2012), Task 13.4 (card 10): nothing was written; the view was of the
        // cart before its latest send. Show the cart now (the same cart, emptied by the send,
        // AC8) and the Order sent banner. As in the conflict branch below, another row's write
        // still in flight keeps its row, and a cart shown at a newer revision is kept.
        const shown = withUnsettledRowsKept(
          orderedLines(err.basket, skuCode),
          get().lines,
          withoutSku(get().pending, skuCode),
        );
        set({
          lines: shown,
          quantityBySku: quantityMap(shown),
          pending: withoutSku(get().pending, skuCode),
          lineConflicts: new Map<string, LineConflict>(),
          ...identityOf(err.basket),
          cartSentAt: err.sentAt,
          orderSent: {
            sentAt: err.sentAt,
            skuCode,
            productName: before?.row.name ?? skuCode,
            quantity:
              mutation.action === 'remove' || mutation.requestedQuantity === 0
                ? null
                : mutation.requestedQuantity,
            notedAt: Date.now(),
          },
        });
        return false;
      }
      if (err instanceof BasketLineConflictError) {
        // Story 8.3 (ADO-2012), Task 10.4: nothing was written. Show the cart as the server
        // has it (the row as it really is now) and record the conflict for the UI; it is not
        // an error, so no generic message is shown.
        const shown = withUnsettledRowsKept(
          orderedLines(err.basket, skuCode),
          get().lines,
          withoutSku(get().pending, skuCode),
        );
        const conflicts = new Map(get().lineConflicts);
        conflicts.set(skuCode, {
          action: mutation.action,
          requestedQuantity: mutation.requestedQuantity,
          sawLine: seenToken !== null,
          currentQuantity: err.currentLine?.quantity ?? null,
          lastShown:
            before === null
              ? null
              : { name: before.row.name ?? null, after: before.position.before },
        });
        set({
          lines: shown,
          quantityBySku: quantityMap(shown),
          pending: withoutSku(get().pending, skuCode),
          lineConflicts: conflicts,
          ...identityOf(err.basket),
        });
        return false;
      }
      // Roll back only this SKU's row, against CURRENT state so a concurrent
      // mutation on another SKU is preserved, and EXPOSE why the write did not
      // land. The error map is read from CURRENT state too, then only this SKU's
      // entry is set.
      const rolledBack = withRowAsSnapshot(get().lines, skuCode, before);
      set({
        lines: rolledBack,
        quantityBySku: quantityMap(rolledBack),
        pending: withoutSku(get().pending, skuCode),
        mutationErrors: withError(get().mutationErrors, skuCode, messageOf(err)),
      });
      return false;
    }
  }

  /**
   * Story 8.3 (ADO-2012), Task 10.3 (D-11): the lines to reconcile to from `state`. A state for
   * the cart on screen whose revision is older than the one shown landed out of order: its
   * lines are older than what is shown, so only the acting SKU's own row is taken from it (the
   * write's own result; the per-SKU queue means no later write of that SKU can have landed),
   * and everything else stays as shown. Any other state is taken whole.
   */
  function orderedLines(state: BasketState, skuCode: string | null): readonly BasketLine[] {
    if (!isOlderThanShown(state)) return state.lines;
    if (skuCode === null) return get().lines;
    return withRowAsSnapshot(get().lines, skuCode, snapshotRow(state.lines, skuCode));
  }

  /** Story 8.3 (ADO-2012), Task 6: the cart revision on screen, or none when no cart is shown
   *  (nothing to compare: the server does not check). */
  function revisionShown(): number | undefined {
    return get().cartId === null ? undefined : get().revision;
  }

  /** ADO-3257 (story D-3): the view of `state`'s cart this page holds, or, when it shows another
   *  cart or none (a reload, a switch back), the revision this tab last showed of it; `null` when
   *  the tab never showed it. */
  function seenCartFor(state: BasketState): SeenCart | null {
    const { cartId } = state;
    if (cartId === null) return null;
    const revision = get().cartId === cartId ? get().revision : shownRevisionOf(cartId);
    return revision === undefined ? null : { cartId, revision };
  }

  /** Story 8.3 (ADO-2012), D-21: show the cart a refused clear or replace sent back, as it is
   *  now, with no marks on lines. A line write still in flight keeps its row; its own response
   *  settles it. */
  function showCartNow(basket: BasketState): void {
    const lines = withUnsettledRowsKept(orderedLines(basket, null), get().lines, get().pending);
    set({
      lines,
      quantityBySku: quantityMap(lines),
      lineConflicts: new Map<string, LineConflict>(),
      ...identityOf(basket),
    });
  }

  function isOlderThanShown(state: BasketState): boolean {
    const shown = get();
    return state.cartId === shown.cartId && state.revision < shown.revision;
  }

  /** The cart identity to show after applying `state`: its own, unless it is older. */
  function identityOf(state: BasketState): Pick<BasketStoreState, 'cartId' | 'revision'> {
    if (isOlderThanShown(state)) return { cartId: get().cartId, revision: get().revision };
    return { cartId: state.cartId, revision: state.revision };
  }

  /** Story 8.2 (ADO-2011): the add behind `add` and `addToNewCart`. No-op (`false`) for a quantity
   *  that is not a positive integer up to the line maximum. */
  function addLine(skuCode: string, quantity: number, keepsOrderSent = false): Promise<boolean> {
    // The upper bound is defence in depth behind the pickers' cap: an over-max add
    // never shows an over-cap optimistic quantity the server's 400 would roll back.
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_LINE_QUANTITY) {
      return Promise.resolve(false);
    }
    return mutateLine({
      skuCode,
      // AC3 gate (defence-in-depth behind the card's disabled button): a supplier
      // `UNAVAILABLE` SKU is NOT orderable — no-op and surface the block. Keys off
      // `status` (DD-S.2 axis 1), NOT the advisory `OUT_OF_STOCK` display band: a
      // numeric-zero AVAILABLE product is still orderable (SL1.0 parity).
      blockedReason: () =>
        useAvailabilityStore.getState().entries[skuCode]?.status === 'UNAVAILABLE'
          ? SUPPLIER_UNAVAILABLE_MESSAGE
          : null,
      // Optimistic: add at `quantity` / sum onto the existing line at once (AC1/AC2).
      applyOptimistic: (current) => withIncrement(current, skuCode, quantity),
      action: 'add',
      requestedQuantity: quantity,
      // Story 8.3 (ADO-2012), D-5: the token of the line the Shopper saw, or none.
      send: (sendCart, seenToken, seenRevision) =>
        addToBasket(skuCode, sendCart, quantity, seenToken, seenRevision),
      keepsOrderSent,
    });
  }

  /**
   * Story 8.2 (ADO-2011) — `setQuantity` / `remove`: a `mutateLine` on a line
   * already in the basket (a failure restores its quantity AND position). No-op
   * for a SKU not in the basket.
   */
  function editLine(
    skuCode: string,
    nextQuantity: number,
    send: LineMutation['send'],
  ): Promise<boolean> {
    if (!get().lines.some((l) => l.skuCode === skuCode)) return Promise.resolve(false);
    return mutateLine({
      skuCode,
      applyOptimistic: (current) => withQuantity(current, skuCode, nextQuantity),
      ...editIntent(nextQuantity),
      send,
    });
  }

  return {
    lines: [],
    cartId: null,
    revision: 0,
    lineConflicts: new Map<string, LineConflict>(),
    orderSent: null,
    cartSentAt: null,
    keptFromSend: null,
    quantityBySku: new Map<string, number>(),
    hydrated: false,
    error: false,
    pending: new Set<string>(),
    mutationErrors: new Map<string, string>(),
    clearing: false,
    clearError: null,
    clearChanged: null,
    addingFromOrder: false,
    autoSend: null,
    autoSendLeadMinutes: null,
    autoSendPending: null,
    autoSendError: null,

    async hydrate(cart) {
      const startedIn = resetGeneration;
      const clearedIn = clearGeneration;
      const autoSendIn = autoSendGeneration;
      // A clear landed while the read was in flight: the store already holds the
      // server's post-clear basket, newer than this read, so the read's lines (or its
      // failure) are dropped and the store counts as loaded. Without this the late
      // read would write the cleared lines back.
      const supersededByClear = (): boolean => {
        if (clearedIn === clearGeneration) return false;
        set({ hydrated: true, error: false });
        return true;
      };
      set({ error: false });
      try {
        const state = await listBasket(cart);
        // A reset while the read was in flight: these lines are no longer the
        // basket on screen (a change of depot or account resets, then hydrates its
        // own). The generation, not the cart, is the guard: a read that crosses
        // A -> B -> A would pass a cart check and land over a write made after the
        // return.
        if (startedIn !== resetGeneration) return;
        if (supersededByClear()) return;
        // The same unsettled-row rule as a write's reconcile: every SKU with a write
        // in flight or queued keeps the row the Shopper sees now (or its absence,
        // restored beside its current neighbours), so a read that raced a write can
        // neither undo an optimistic change nor seed the next stepper tap from a
        // pre-write value. That write's own response (or rollback) settles the row.
        // Story 8.3 (ADO-2012), D-11: an older read of the cart on screen changes nothing.
        const lines = withUnsettledRowsKept(orderedLines(state, null), get().lines, get().pending);
        // Enabler E8-G (ADO-3109), AC16: what the cart kept from its latest send, from a read
        // that carries it and is not older than the cart shown.
        const kept = isOlderThanShown(state) ? null : keptFromSendOf(state, cart);
        // ADO-3257 (story D-2, D-6): the cart was sent after the cart this page (or, after a
        // reload, this tab) showed: say when, as card 10 does, with no product. Judged against the
        // cart shown BEFORE this read is applied.
        const sentAt = isOlderThanShown(state) ? null : sentSinceSeen(seenCartFor(state), state);
        set({
          lines,
          quantityBySku: quantityMap(lines),
          hydrated: true,
          error: false,
          ...identityOf(state),
          ...(kept === null ? {} : { keptFromSend: kept }),
          // Story 8.15 (ADO-2024): the opt-in and the lead time, from a read that carries them
          // and is not older than the cart shown; the opt-in only from a read that started after
          // the latest arm or disarm landed.
          ...(isOlderThanShown(state) ||
          state.autoSend === undefined ||
          autoSendIn !== autoSendGeneration
            ? {}
            : { autoSend: state.autoSend }),
          ...(state.autoSendLeadMinutes === undefined
            ? {}
            : { autoSendLeadMinutes: state.autoSendLeadMinutes }),
          ...(sentAt === null
            ? {}
            : {
                cartSentAt: sentAt,
                orderSent: {
                  sentAt,
                  skuCode: null,
                  productName: null,
                  quantity: null,
                  notedAt: Date.now(),
                },
              }),
        });
      } catch {
        // A hydration failure must not blank the basket or crash a listing — leave
        // the current state (empty on first load) but FLAG the error so a screen can
        // surface a retry instead of an infinite spinner. (Story 8.2: the durable
        // route now exists, so a 404 is a real failure, not "feature not deployed".)
        if (startedIn !== resetGeneration) return;
        if (supersededByClear()) return;
        set({ error: true });
      }
    },

    async add(skuCode, quantity = 1) {
      await addLine(skuCode, quantity);
    },

    async addToNewCart() {
      const notice = get().orderSent;
      // ADO-3257: a read's notice has no product, so nothing to add.
      if (notice === null || notice.skuCode === null || notice.quantity === null) return false;
      const landed = await addLine(notice.skuCode, notice.quantity, true);
      if (landed && get().orderSent === notice) set({ orderSent: null });
      return landed;
    },

    async setQuantity(skuCode, quantity) {
      if (!Number.isInteger(quantity) || quantity < 0) return false;
      if (get().pending.has(skuCode)) return queueEdit(skuCode, quantity);
      return editLine(skuCode, quantity, (sendCart, seenToken, seenRevision) =>
        updateBasketLineQuantity(skuCode, quantity, sendCart, seenToken ?? '', seenRevision),
      );
    },

    async remove(skuCode) {
      // Queued as quantity 0, which the PATCH treats as a removal.
      if (get().pending.has(skuCode)) return queueEdit(skuCode, 0);
      return editLine(skuCode, 0, (sendCart, seenToken, seenRevision) =>
        removeBasketLine(skuCode, sendCart, seenToken ?? '', seenRevision),
      );
    },

    async clear() {
      if (get().clearing) return false;
      // A line write or a previous-order add is still in flight: its reconcile would
      // land AFTER the clear and resurrect stale lines, so refuse (the confirm is
      // disabled meanwhile too, which is the visible reason). Drop any earlier clear's
      // error so the dialog does not show an unrelated, stale failure.
      if (get().pending.size > 0 || get().addingFromOrder) {
        set({ clearError: null });
        return false;
      }
      // No active depot: surface the depot prompt, as `mutateLine` does on the same
      // guard, so the confirm dialog shows why nothing happened.
      const cart = sessionCartTarget();
      if (cart === null) {
        set({ clearError: NO_DEPOT_SELECTED_MESSAGE });
        return false;
      }

      const generation = resetGeneration;
      // Park-and-switch (the `mutateLine` guard): the store was reset (every change
      // of depot or account resets it) while the clear was in flight, so the response
      // belongs to a basket no longer shown. Drop it and leave `clearing` alone: the
      // reset already released this clear's mutex and a newer clear may hold it now.
      const superseded = (): boolean => {
        if (generation !== resetGeneration) return true;
        const current = sessionCartTarget();
        if (current !== null && cartTargetKey(current) === cartTargetKey(cart)) return false;
        // Defence in depth: the cart moved without a reset, so release the mutex.
        set({ clearing: false });
        return true;
      };
      set({ clearing: true, clearError: null, clearChanged: null });
      try {
        // Story 8.3 (ADO-2012), Task 6 (D-21, D-22): the revision on screen, so a cart that
        // changed since is not cleared unseen.
        // Story 8.3 (ADO-2012), Task 13.2: tried once more if a send held the cart.
        const state = await tracked(
          withOneRetryIfSending(
            () => clearBasket(cart, revisionShown()),
            () => generation === resetGeneration,
          ),
        );
        if (superseded()) return false;
        // Any read in flight now holds the pre-clear lines: `hydrate` drops it.
        clearGeneration += 1;
        // Story 8.3 (ADO-2012), D-11: an answer older than the revision shown changes no line.
        const lines = orderedLines(state, null);
        set({
          lines,
          quantityBySku: quantityMap(lines),
          mutationErrors: new Map<string, string>(),
          // Story 8.3 (ADO-2012), decided with the user: the Shopper's own clear changes every
          // line, so it ends every message, a Removed Line included.
          lineConflicts: new Map<string, LineConflict>(),
          clearing: false,
          ...identityOf(state),
        });
        return true;
      } catch (err) {
        // Same guard on the failure path: the error belongs to the previous basket.
        if (superseded()) return false;
        if (err instanceof CartChangedSinceSeenError) {
          // Story 8.3 (ADO-2012), card 06 (D-21): nothing was cleared. Show the cart as it is
          // now, with no marks on lines, and give the dialog its counts.
          showCartNow(err.basket);
          set({
            clearing: false,
            clearChanged: { lineCount: err.lineCount, caseCount: err.caseCount },
          });
          return false;
        }
        // Nothing to roll back (non-optimistic): keep the lines, expose the reason.
        set({ clearing: false, clearError: messageOf(err) });
        return false;
      }
    },

    async addFromOrder(orderId, request) {
      // Two full-post-state reconciles must not race: a line write, a clear, or
      // another order being added in flight refuses this one before anything is sent.
      if (get().addingFromOrder || get().clearing || get().pending.size > 0) {
        return { kind: 'busy' };
      }
      const cart = sessionCartTarget();
      if (cart === null) return { kind: 'failed', message: NO_DEPOT_SELECTED_MESSAGE };

      const startedIn = resetGeneration;
      set({ addingFromOrder: true });
      try {
        // Story 8.3 (ADO-2012), D-23 (card 08): an ADD carries what this cart shows, so a line
        // that changed since is left out rather than summed onto (nothing is doubled).
        // Before the cart has been read nothing of it was seen, so no view is sent and the server
        // adds on top as 8.7 does (an empty view would leave out every line already there).
        // Task 6 (card 07): a REPLACE carries the revision on screen.
        const seenRevision = revisionShown();
        const sent: AddFromPreviousOrderRequest =
          request.mode === 'ADD'
            ? {
                ...request,
                ...(get().hydrated
                  ? {
                      seenLines: get().lines.flatMap((line) =>
                        line.token === undefined
                          ? []
                          : [{ skuCode: line.skuCode, token: line.token }],
                      ),
                    }
                  : {}),
              }
            : { ...request, ...(seenRevision === undefined ? {} : { seenRevision }) };
        const result = await tracked(addFromPreviousOrder(cart, orderId, sent));
        // A depot switch (or sign-out) reset the store while the request was in flight:
        // the answer is for a basket no longer on screen, and the reset already cleared
        // `addingFromOrder`.
        if (startedIn !== resetGeneration) return { kind: 'dropped' };
        // A line write started since keeps the row the Shopper sees; its own response
        // settles it.
        const lines = withUnsettledRowsKept(
          orderedLines(result.basket, null),
          get().lines,
          get().pending,
        );
        set({
          lines,
          quantityBySku: quantityMap(lines),
          addingFromOrder: false,
          // Story 8.3 (ADO-2012), decided with the user: a replace changes every line, so it ends
          // every message, as a clear does; an add leaves the others' messages alone.
          ...(request.mode === 'REPLACE' ? { lineConflicts: new Map<string, LineConflict>() } : {}),
          ...identityOf(result.basket),
        });
        return { kind: 'added', added: result.added, notAdded: result.notAdded };
      } catch (err) {
        if (startedIn !== resetGeneration) return { kind: 'dropped' };
        set({ addingFromOrder: false });
        // Story 8.3 (ADO-2012), Task 13.2: a send holds the cart: the usual "still updating".
        if (err instanceof CartSendingError) return { kind: 'busy' };
        if (err instanceof CartChangedSinceSeenError) {
          // Story 8.3 (ADO-2012), card 07 (D-21): nothing was replaced; show the cart now.
          showCartNow(err.basket);
          return { kind: 'changed', lineCount: err.lineCount, caseCount: err.caseCount };
        }
        return { kind: 'failed', message: messageOf(err) };
      }
    },

    reset() {
      resetGeneration += 1;
      queuedAutoSend = null;
      for (const skuCode of [...queuedEdits.keys()]) settleQueuedEdit(skuCode, false);
      newCartStartedBySku.clear();
      set({
        lines: [],
        cartId: null,
        revision: 0,
        lineConflicts: new Map<string, LineConflict>(),
        orderSent: null,
        cartSentAt: null,
        keptFromSend: null,
        quantityBySku: new Map<string, number>(),
        hydrated: false,
        error: false,
        pending: new Set<string>(),
        mutationErrors: new Map<string, string>(),
        clearing: false,
        clearError: null,
        clearChanged: null,
        addingFromOrder: false,
        autoSend: null,
        autoSendLeadMinutes: null,
        autoSendPending: null,
        autoSendError: null,
      });
    },

    async setAutoSend(armed, choices) {
      const cart = sessionCartTarget();
      const { cartId } = get();
      if (cart === null || cartId === null) return;
      if (get().autoSendPending !== null) {
        queuedAutoSend = { armed, choices };
        set({ autoSendPending: armed, autoSendError: null });
        return;
      }
      const startedIn = resetGeneration;
      const armedBefore = get().autoSend !== null;
      set({ autoSendPending: armed, autoSendError: null });
      try {
        let autoSend: AutoSendState | null = null;
        if (armed) autoSend = await armAutoSend(cart, cartId, choices ?? {});
        else await disarmAutoSend(cart);
        if (startedIn !== resetGeneration) return;
        autoSendGeneration += 1;
        set({ autoSend, autoSendPending: null });
      } catch (error) {
        if (startedIn !== resetGeneration) return;
        queuedAutoSend = null;
        set({ autoSendPending: null, autoSendError: autoSendErrorOf(error, armed, armedBefore) });
        // The cart on screen is not the current one: read it, so a retry names the right cart.
        if (
          error instanceof AutoSendRefusedError &&
          error.problemType === AUTO_SEND_CART_CHANGED_PROBLEM
        ) {
          void get().hydrate(cart);
        }
        return;
      }
      const next = queuedAutoSend;
      queuedAutoSend = null;
      if (next !== null) await get().setAutoSend(next.armed, next.choices);
    },

    dismissAutoSendError() {
      if (get().autoSendError !== null) set({ autoSendError: null });
    },

    dismissClearChanged() {
      if (get().clearChanged !== null) set({ clearChanged: null });
    },

    dismissOrderSent() {
      if (get().orderSent !== null) set({ orderSent: null });
    },

    generation() {
      return resetGeneration;
    },

    async writesSettled() {
      await Promise.allSettled([...writesInFlight]);
    },

    noteCartSent(sentAt, startedIn) {
      if (startedIn !== resetGeneration || get().cartSentAt === sentAt) return;
      set({ cartSentAt: sentAt });
    },

    markChangedWhileReviewing(reviewed, current, startedIn) {
      // The store was reset since the send started: its refusal is for a cart no longer shown.
      if (startedIn !== resetGeneration) return null;
      const { pending } = get();
      // Story 8.3 (ADO-2012), D-11: a refusal older than the revision shown marks against the
      // lines shown, which are newer. A row whose own write is still unsettled keeps what the
      // Shopper sees, and is not marked: its own response settles it.
      const shownNow = orderedLines(current, null);
      const now = new Map(shownNow.map((line) => [line.skuCode, line] as const));
      const seen = new Set(reviewed.map((line) => line.skuCode));
      // The lifetime rule: a mark lasts until its action, a change to its line, or leaving the
      // page, so a second refusal keeps what is shown and adds or updates only what changed again.
      const marks = new Map<string, LineConflict>(get().lineConflicts);
      let changed = 0;
      reviewed.forEach((line, index) => {
        if (pending.has(line.skuCode)) return;
        const after = reviewed[index - 1]?.skuCode ?? null;
        const nowLine = now.get(line.skuCode);
        if (nowLine !== undefined && nowLine.quantity === line.quantity) return;
        changed += 1;
        marks.set(line.skuCode, {
          action: 'review',
          requestedQuantity: line.quantity,
          sawLine: true,
          currentQuantity: nowLine?.quantity ?? null,
          lastShown: { name: line.name ?? null, after },
        });
      });
      for (const line of shownNow) {
        if (seen.has(line.skuCode) || pending.has(line.skuCode)) continue;
        changed += 1;
        marks.set(line.skuCode, {
          action: 'review',
          requestedQuantity: 0,
          sawLine: false,
          currentQuantity: line.quantity,
          lastShown: null,
        });
      }
      const lines = withUnsettledRowsKept(shownNow, get().lines, pending);
      set({
        lines,
        quantityBySku: quantityMap(lines),
        lineConflicts: marks,
        ...identityOf(current),
      });
      return changed;
    },

    takeNewCartStarted(skuCode) {
      const sentAt = newCartStartedBySku.get(skuCode) ?? null;
      newCartStartedBySku.delete(skuCode);
      return sentAt;
    },

    dismissConflict(skuCode) {
      set({ lineConflicts: withoutConflict(get().lineConflicts, skuCode) });
    },

    dismissConflicts() {
      if (get().lineConflicts.size > 0) set({ lineConflicts: new Map<string, LineConflict>() });
    },
  };
});

// Story 8.2 (ADO-2011): the lines belong to the cart they were loaded for. The
// cart trigger and side panel are on every full-header screen, and the depot can
// be switched from any of them (the header selector calls `setActiveDepot`
// directly), so the store drops its lines on every change of the session cart
// rather than relying on each screen to do it. Story 8.4 (ADO-2013), AC4: the cart is
// the depot AND the account, so changing account at the same Value Centre drops them
// too (compared by code, so a choices refresh handing back an equal account is no
// change). An in-flight write or read for the old cart is then dropped by the
// generation check in `runLine` and `hydrate`, which `reset()` bumps. The subscription
// lives for the whole app (and the whole spec run), so a spec that seeds basket
// lines must set the session depot first.
useSessionStore.subscribe((session, previousSession) => {
  if (sessionCartKey(session) !== sessionCartKey(previousSession)) {
    useBasketStore.getState().reset();
  }
});

// ADO-3257 (story D-3): the tab remembers the revision it shows of each cart, so a read after a
// reload can still tell a send made after it.
useBasketStore.subscribe((basket, previousBasket) => {
  if (basket.cartId === null) return;
  if (basket.cartId === previousBasket.cartId && basket.revision === previousBasket.revision)
    return;
  rememberShownRevision(basket.cartId, basket.revision);
});
